(() => {
  // ---- Config (flip useMock=false when backend is ready)
  window.OUTSTRIDE_CONFIG = Object.assign({
    useMock: true,
    currency: document.documentElement.getAttribute('lang')?.toUpperCase() === 'EN' ? (window.Shopify?.currency?.active || 'INR') : (window.Shopify?.currency?.active || 'INR'),
    appProxyPath: '/apps/outstride/balance',
    capPercent: 100, // preview cap; real cap comes from backend response later
    subscriptionProductHandle: 'subscription'
  }, window.OUTSTRIDE_CONFIG || {});

  const fmt = (cents, cur) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency: cur || window.OUTSTRIDE_CONFIG.currency }).format((Number(cents || 0))/100);

  function computeUsableCents(balanceCents, subtotalCents, capPercent) {
    const cap = Math.floor((subtotalCents * (capPercent ?? 100)) / 100);
    return Math.max(0, Math.min(balanceCents, cap));
  }

  async function fetchLive({ variantPriceCents, cartSubtotalCents }) {
    // When called from theme, App Proxy auto-adds signature
    const url = `${window.OUTSTRIDE_CONFIG.appProxyPath}?variant_price_cents=${variantPriceCents}&cart_subtotal_cents=${cartSubtotalCents}`;
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('proxy_error');
    return res.json();
  }

  async function fetchMock({ loggedIn, subscribed, balanceCents, variantPriceCents, cartSubtotalCents, capPercent }) {
    const subActive = !!subscribed;
    const subtotal = Number(cartSubtotalCents || variantPriceCents || 0);
    const usable = subActive ? computeUsableCents(Number(balanceCents||0), subtotal, capPercent) : 0;
    return Promise.resolve({
      loggedIn: !!loggedIn,
      subscriptionActive: subActive,
      balanceCents: Number(balanceCents || 0),
      usableCents: usable,
      newOfferPriceCents: Math.max(0, subtotal - usable),
      currency: window.OUTSTRIDE_CONFIG.currency,
      capPercent: Number(capPercent || window.OUTSTRIDE_CONFIG.capPercent)
    });
  }

  async function getOutstrideContext({ el, variantPriceCents, cartSubtotalCents }) {
    const loggedInAttr = el?.dataset?.loggedIn;
    const loggedIn = loggedInAttr === 'true';
    const subscribedAttr = el?.dataset?.subscribed; // only used in mock
    const balanceAttr = el?.dataset?.balanceCents;  // only used in mock
    const capAttr = el?.dataset?.capPercent;       // only used in mock

    if (!window.OUTSTRIDE_CONFIG.useMock) {
      try {
        return await fetchLive({ variantPriceCents, cartSubtotalCents });
      } catch (e) {
        // Silent fallback to mock if proxy not ready
      }
    }
    return fetchMock({
      loggedIn,
      subscribed: subscribedAttr === 'true',
      balanceCents: Number(balanceAttr || 0),
      variantPriceCents,
      cartSubtotalCents,
      capPercent: Number(capAttr || window.OUTSTRIDE_CONFIG.capPercent)
    });
  }

  // ---- PDP block
  function initPdp(el) {
    const orig = el.querySelector('[data-os-orig]');
    const neu  = el.querySelector('[data-os-new]');
    const note = el.querySelector('[data-os-note]');
    const banner = el.querySelector('.outstride-banner');
    const subBtn = el.querySelector('[data-os-subscribe]');

    const cur = el.dataset.currency || window.OUTSTRIDE_CONFIG.currency;
    const form = el.closest('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');

    const readVariantPrice = () => {
      // Prefer data attr from snippet; fallback to price on page if needed
      const fromData = Number(el.dataset.variantPriceCents || 0);
      if (fromData > 0) return fromData;

      const priceNode = document.querySelector('[data-product-price] [data-price]') || document.querySelector('[itemprop="price"]');
      if (priceNode) {
        const n = Number(priceNode.getAttribute('content') || priceNode.textContent.replace(/[^\d.]/g, ''));
        if (!Number.isNaN(n)) return Math.round(n * 100);
      }
      return 0;
    };

    const render = async () => {
      const variantPriceCents = readVariantPrice();
      const subtotalCents = Number(el.dataset.cartSubtotalCents || variantPriceCents || 0);
      const ctx = await getOutstrideContext({ el, variantPriceCents, cartSubtotalCents: subtotalCents });

      if (!ctx.loggedIn) {
        banner.style.display = 'block';
        orig.textContent = fmt(variantPriceCents, cur);
        neu.textContent  = fmt(variantPriceCents, cur);
        note.textContent = 'Login to see OutStride Cash pricing';
        return;
      }

      if (!ctx.subscriptionActive) {
        banner.style.display = 'block';
        orig.textContent = fmt(variantPriceCents, cur);
        neu.textContent  = fmt(variantPriceCents, cur);
        note.textContent = `Balance: ${fmt(ctx.balanceCents, cur)} (subscribe to use)`;
        return;
      }

      banner.style.display = 'none';
      orig.textContent = fmt(subtotalCents, cur);
      neu.textContent  = fmt(ctx.newOfferPriceCents, cur);
      note.textContent = `Using ${fmt(ctx.usableCents, cur)} OutStride Cash`;
    };

    // React to variant change in most OS 2.0 themes:
    const watchVariant = () => {
      // Dawn dispatches a "variant:change" on the product form; support a few patterns:
      document.addEventListener('variant:change', render);
      document.addEventListener('variant:changed', render);
      if (form) form.addEventListener('change', (e) => {
        if (e.target && (e.target.name?.includes('option') || e.target.name === 'id')) render();
      });
      // As a fallback, observe the price DOM for changes
      const priceEl = document.querySelector('[data-product-price]') || document.querySelector('[itemprop="price"]') || el;
      const mo = new MutationObserver(() => render());
      mo.observe(priceEl, { subtree: true, childList: true, characterData: true });
    };

    render();
    watchVariant();

    // Optional dev mini-panel for mock tweaking
    if (window.OUTSTRIDE_CONFIG.useMock && !document.querySelector('.outstride-dev')) {
      const d = document.createElement('div');
      d.className = 'outstride-dev';
      d.innerHTML = `
        <div><strong>OutStride Mock</strong></div>
        <label><input type="checkbox" data-dev-logged /> logged</label>
        <label><input type="checkbox" data-dev-sub /> subscribed</label>
        <label>balance <input type="number" min="0" step="1" data-dev-bal /></label>
      `;
      document.body.appendChild(d);
      const chkL = d.querySelector('[data-dev-logged]');
      const chkS = d.querySelector('[data-dev-sub]');
      const inpB = d.querySelector('[data-dev-bal]');
      chkL.checked = el.dataset.loggedIn === 'true';
      chkS.checked = el.dataset.subscribed === 'true';
      inpB.value = el.dataset.balanceCents || 0;
      const sync = () => { el.dataset.loggedIn = chkL.checked ? 'true' : 'false';
                           el.dataset.subscribed = chkS.checked ? 'true' : 'false';
                           el.dataset.balanceCents = String(Math.max(0, parseInt(inpB.value||'0',10)));
                           render(); };
      chkL.onchange = chkS.onchange = inpB.oninput = sync;
    }

    if (subBtn) subBtn.addEventListener('click', (e) => {
      // let it navigate; you can add analytics here
    });
  }

  // ---- Cart block
  function initCart(el) {
    const line = el.querySelector('[data-os-cart-line]');
    const cur = el.dataset.currency || window.OUTSTRIDE_CONFIG.currency;

    const readCartSubtotal = () => {
      // value from snippet
      const t = Number(el.dataset.cartSubtotalCents || 0);
      if (t > 0) return t;
      // try DOM
      const node = document.querySelector('[data-cart-subtotal],[data-checkout-subtotal-price-target]');
      if (node) {
        const n = Number(node.getAttribute('data-cart-subtotal') || node.textContent.replace(/[^\d.]/g, ''));
        if (!Number.isNaN(n)) return Math.round(n * 100);
      }
      return 0;
    };

    const render = async () => {
      const subtotal = readCartSubtotal();
      const ctx = await getOutstrideContext({ el, variantPriceCents: subtotal, cartSubtotalCents: subtotal });

      if (!ctx.loggedIn) {
        line.textContent = 'Login to use OutStride Cash';
        return;
      }
      if (!ctx.subscriptionActive) {
        line.textContent = `Balance: ${fmt(ctx.balanceCents, cur)} (subscribe to use)`;
        return;
      }
      if (ctx.usableCents > 0) {
        line.textContent = `OutStride Cash discount: -${fmt(ctx.usableCents, cur)} (est.)`;
      } else {
        line.textContent = `No OutStride Cash applied`;
      }
    };

    // re-render when quantities change (common events)
    document.addEventListener('cart:refresh', render);
    document.addEventListener('change', (e) => {
      if (e.target && (e.target.name?.includes('updates') || e.target.name === 'quantity')) render();
    });
    render();
  }

  // boot
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-outstride-cash="pdp"]').forEach(initPdp);
    document.querySelectorAll('[data-outstride-cash="cart"]').forEach(initCart);
  });
})();
